import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getProjectById, updatePaymentStatus, updateDeveloperPaymentStatus, updateOtherExpenseStatus } from '../../services/projectService';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { PageLoading } from '../ui/Loading';
import { AlertCircle } from 'lucide-react';

// Import components
import ProjectDetailHeader from './ProjectDetailHeader';
import ProjectProfitOverview from './ProjectProfitOverview';
import ProjectFinancialSummary from './ProjectFinancialSummary';
import ProjectDataTables from './ProjectDataTables';

const ProjectDetail = () => {
  const { id } = useParams();
  
  const [project, setProject] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ 
    open: false, 
    title: '', 
    message: '', 
    onConfirm: null,
    confirmText: 'ยืนยัน',
    cancelText: 'ยกเลิก'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProject = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await getProjectById(id);
      if (error) throw error;
      
      setProject(data);
    } catch (err) {
      console.error('Error loading project:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadProject();
  }, [id, loadProject]);

  const showConfirmDialog = (title, message, onConfirm, confirmText = 'ยืนยัน', cancelText = 'ยกเลิก') => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      title: '',
      message: '',
      onConfirm: null,
      confirmText: 'ยืนยัน',
      cancelText: 'ยกเลิก'
    });
  };

  const handlePaymentStatus = (paymentId, paid) => {
    const payment = project.payment_schedule.find(p => p.id === paymentId);
    if (!payment) {
      console.error('Payment not found:', paymentId);
      return;
    }
    
    const action = paid ? 'ยืนยันการชำระ' : 'ยกเลิกการชำระ';
    const message = paid 
      ? `คุณต้องการยืนยันการชำระงวด ${payment.installment_number || payment.installment} จำนวน ${payment.amount} หรือไม่?`
      : `คุณต้องการยกเลิกการชำระงวด ${payment.installment_number || payment.installment} จำนวน ${payment.amount} หรือไม่?`;
    
    showConfirmDialog(
      action,
      message,
      async () => {
        try {
          setLoading(true);
          const { error } = await updatePaymentStatus(id, paymentId, paid);
          if (error) throw error;
          
          toast.success(paid ? 'ยืนยันการชำระเรียบร้อย' : 'ยกเลิกการชำระเรียบร้อย');
          await loadProject();
        } catch (err) {
          console.error('Error updating payment status:', err);
          toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะการชำระ');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleDeveloperPaymentStatus = (developerId, paid) => {
    const developer = project.developer_payments.find(d => d.id === developerId);
    if (!developer) {
      console.error('Developer not found:', developerId);
      return;
    }
    
    const developerName = developer.developer_name || developer.name || 'Developer';
    const action = paid ? 'ยืนยันการจ่าย' : 'ยกเลิกการจ่าย';
    const message = paid 
      ? `คุณต้องการยืนยันการจ่ายค่าจ้างให้ ${developerName} จำนวน ${developer.amount} หรือไม่?`
      : `คุณต้องการยกเลิกการจ่ายค่าจ้างให้ ${developerName} จำนวน ${developer.amount} หรือไม่?`;
    
    showConfirmDialog(
      action,
      message,
      async () => {
        try {
          setLoading(true);
          const { error } = await updateDeveloperPaymentStatus(id, developerId, paid);
          if (error) throw error;
          
          toast.success(paid ? 'ยืนยันการจ่ายเรียบร้อย' : 'ยกเลิกการจ่ายเรียบร้อย');
          await loadProject();
        } catch (err) {
          console.error('Error updating developer payment status:', err);
          toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะการจ่าย');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleOtherExpenseStatus = (expenseId, paid) => {
    const expense = project.other_expenses.find(e => e.id === expenseId);
    if (!expense) {
      console.error('Expense not found:', expenseId);
      return;
    }
    
    const action = paid ? 'ยืนยันการจ่าย' : 'ยกเลิกการจ่าย';
    const message = paid 
      ? `คุณต้องการยืนยันการจ่ายค่าใช้จ่าย ${expense.name || expense.expense_name} จำนวน ${expense.amount} หรือไม่?`
      : `คุณต้องการยกเลิกการจ่ายค่าใช้จ่าย ${expense.name || expense.expense_name} จำนวน ${expense.amount} หรือไม่?`;
    
    showConfirmDialog(
      action,
      message,
      async () => {
        try {
          setLoading(true);
          const { error } = await updateOtherExpenseStatus(id, expenseId, paid);
          if (error) throw error;
          
          toast.success(paid ? 'ยืนยันการจ่ายเรียบร้อย' : 'ยกเลิกการจ่ายเรียบร้อย');
          await loadProject();
        } catch (err) {
          console.error('Error updating other expense status:', err);
          toast.error('เกิดข้อผิดพลาดในการอัปเดตสถานะการจ่าย');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  const handleEdit = () => {
    window.location.href = `/projects/${id}/edit`;
  };

  if (loading) {
    return <PageLoading message="กำลังโหลดรายละเอียดโปรเจค..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            เกิดข้อผิดพลาด: {error}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            ไม่พบข้อมูลโปรเจค
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <ProjectDetailHeader project={project} onEdit={handleEdit} />

      {/* Main Content */}
      <div className="space-y-6">
        {/* Profit Overview - ด้านบน */}
        <ProjectProfitOverview project={project} />

        {/* Financial Summary - รวมสรุปการเงิน, การคำนวณสำคัญ, ข้อมูลพื้นฐาน, และสถิติด่วน */}
        <ProjectFinancialSummary project={project} />

        {/* Data Tables */}
        <ProjectDataTables 
          project={project}
          onPaymentStatus={handlePaymentStatus}
          onDeveloperPayment={handleDeveloperPaymentStatus}
          onExpensePayment={handleOtherExpenseStatus}
        />
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={hideConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription>
              {confirmDialog.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={hideConfirmDialog}>
              {confirmDialog.cancelText}
            </Button>
            <Button onClick={() => {
              if (confirmDialog.onConfirm) {
                confirmDialog.onConfirm();
              }
              hideConfirmDialog();
            }}>
              {confirmDialog.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
